/**
 * 티켓 상세 페이지
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Row, Col, Badge, Button, Modal, Alert, Table } from 'react-bootstrap';
import AdminLayout from '../../components/layout/AdminLayout';
import { ticketService } from '../../services/ticketService';
import { Ticket, TicketStatus, TicketType } from '../../types/ticket';
import { format } from 'date-fns';

const TicketDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);

  const { data: ticket, isLoading, error } = useQuery({
    queryKey: ['ticket', id],
    queryFn: () => ticketService.getTicket(Number(id)),
    enabled: !!id,
  });

  const { data: entryHistory } = useQuery({
    queryKey: ['ticket', id, 'entries'],
    queryFn: () => ticketService.getTicketEntryHistory(Number(id)),
    enabled: !!id && !!ticket,
  });

  const cancelMutation = useMutation({
    mutationFn: () => ticketService.cancelTicket(Number(id)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket', id] });
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      setShowCancelModal(false);
    },
  });

  const getStatusBadge = (status: TicketStatus) => {
    const statusMap: Record<TicketStatus, { variant: string; label: string }> = {
      issued: { variant: 'success', label: '발행됨' },
      used: { variant: 'info', label: '사용됨' },
      expired: { variant: 'warning', label: '만료됨' },
      cancelled: { variant: 'danger', label: '취소됨' },
    };

    const statusInfo = statusMap[status] || { variant: 'secondary', label: status };
    return <Badge bg={statusInfo.variant}>{statusInfo.label}</Badge>;
  };

  const getTypeLabel = (type: TicketType) => {
    const typeMap: Record<TicketType, string> = {
      adult: '성인',
      youth: '청소년',
      disabled: '장애인',
      infant: '유아',
    };
    return typeMap[type] || type;
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="text-center py-5">
          <p style={{ color: '#fff' }}>로딩 중...</p>
        </div>
      </AdminLayout>
    );
  }

  if (error || !ticket) {
    return (
      <AdminLayout>
        <div className="text-center py-5">
          <Alert variant="danger">티켓 정보를 불러올 수 없습니다.</Alert>
          <Button variant="secondary" onClick={() => navigate('/admin/tickets')}>
            목록으로
          </Button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 style={{ color: '#fff' }}>티켓 상세</h1>
          <div>
            <Button variant="secondary" onClick={() => navigate('/admin/tickets')} className="me-2">
              목록으로
            </Button>
            {ticket.qr_code && (
              <Button variant="info" onClick={() => setShowQRModal(true)} className="me-2">
                QR 코드 보기
              </Button>
            )}
            {ticket.status !== 'cancelled' && ticket.status !== 'used' && (
              <Button variant="danger" onClick={() => setShowCancelModal(true)}>
                티켓 취소
              </Button>
            )}
          </div>
        </div>

        <Row>
          <Col md={8}>
            <Card className="mb-4">
              <Card.Header>
                <h5 className="mb-0">티켓 정보</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <div className="detail-item mb-3">
                      <label className="detail-label">티켓번호</label>
                      <div className="detail-value">{ticket.ticket_number}</div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="detail-item mb-3">
                      <label className="detail-label">상태</label>
                      <div className="detail-value">{getStatusBadge(ticket.status)}</div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="detail-item mb-3">
                      <label className="detail-label">티켓 유형</label>
                      <div className="detail-value">{getTypeLabel(ticket.ticket_type)}</div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="detail-item mb-3">
                      <label className="detail-label">예약번호</label>
                      <div className="detail-value">
                        {ticket.booking?.booking_number || '-'}
                      </div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="detail-item mb-3">
                      <label className="detail-label">방문일시</label>
                      <div className="detail-value">
                        {ticket.booking?.visit_date 
                          ? format(new Date(ticket.booking.visit_date), 'yyyy년 MM월 dd일')
                          : '-'}
                        {ticket.booking?.visit_time && ` ${ticket.booking.visit_time}`}
                      </div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="detail-item mb-3">
                      <label className="detail-label">발행일시</label>
                      <div className="detail-value">
                        {format(new Date(ticket.issued_at), 'yyyy년 MM월 dd일 HH:mm')}
                      </div>
                    </div>
                  </Col>
                  {ticket.expires_at && (
                    <Col md={6}>
                      <div className="detail-item mb-3">
                        <label className="detail-label">만료일시</label>
                        <div className="detail-value">
                          {format(new Date(ticket.expires_at), 'yyyy년 MM월 dd일 HH:mm')}
                        </div>
                      </div>
                    </Col>
                  )}
                </Row>
              </Card.Body>
            </Card>

            <Card className="mb-4">
              <Card.Header>
                <h5 className="mb-0">상품 정보</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <div className="detail-item mb-3">
                      <label className="detail-label">상품명</label>
                      <div className="detail-value">
                        {ticket.hall?.name || ticket.package?.name || '-'}
                      </div>
                    </div>
                  </Col>
                  {ticket.allowed_hall_ids && ticket.allowed_hall_ids.length > 0 && (
                    <Col md={6}>
                      <div className="detail-item mb-3">
                        <label className="detail-label">입장 가능 관</label>
                        <div className="detail-value">
                          {ticket.allowed_hall_ids.length}개 관
                        </div>
                      </div>
                    </Col>
                  )}
                </Row>
              </Card.Body>
            </Card>

            <Card>
              <Card.Header>
                <h5 className="mb-0">입장 정보</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <div className="detail-item mb-3">
                      <label className="detail-label">입장 횟수</label>
                      <div className="detail-value">
                        {ticket.remaining_entry_count} / {ticket.total_entry_count}
                      </div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="detail-item mb-3">
                      <label className="detail-label">재입장 허용</label>
                      <div className="detail-value">
                        {ticket.reentry_allowed ? '예' : '아니오'}
                      </div>
                    </div>
                  </Col>
                  {ticket.first_entry_at && (
                    <Col md={6}>
                      <div className="detail-item mb-3">
                        <label className="detail-label">최초 입장일시</label>
                        <div className="detail-value">
                          {format(new Date(ticket.first_entry_at), 'yyyy년 MM월 dd일 HH:mm')}
                        </div>
                      </div>
                    </Col>
                  )}
                  {ticket.last_entry_at && (
                    <Col md={6}>
                      <div className="detail-item mb-3">
                        <label className="detail-label">최종 입장일시</label>
                        <div className="detail-value">
                          {format(new Date(ticket.last_entry_at), 'yyyy년 MM월 dd일 HH:mm')}
                        </div>
                      </div>
                    </Col>
                  )}
                </Row>
              </Card.Body>
            </Card>

            {entryHistory && entryHistory.length > 0 && (
              <Card className="mt-4">
                <Card.Header>
                  <h5 className="mb-0">입장 이력</h5>
                </Card.Header>
                <Card.Body>
                  <Table hover className="data-table">
                    <thead>
                      <tr>
                        <th>입장일시</th>
                        <th>관명</th>
                        <th>퇴장일시</th>
                      </tr>
                    </thead>
                    <tbody>
                      {entryHistory.map((entry) => (
                        <tr key={entry.id}>
                          <td>{format(new Date(entry.entry_time), 'yyyy-MM-dd HH:mm:ss')}</td>
                          <td>{entry.hall?.name || '-'}</td>
                          <td>{entry.exit_time ? format(new Date(entry.exit_time), 'yyyy-MM-dd HH:mm:ss') : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            )}
          </Col>

          <Col md={4}>
            <Card>
              <Card.Header>
                <h5 className="mb-0">바우처 정보</h5>
              </Card.Header>
              <Card.Body>
                <div className="detail-item mb-3">
                  <label className="detail-label">바우처번호</label>
                  <div className="detail-value">
                    {ticket.voucher?.voucher_number || '-'}
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* QR 코드 모달 */}
        <Modal show={showQRModal} onHide={() => setShowQRModal(false)} size="sm">
          <Modal.Header closeButton>
            <Modal.Title>QR 코드</Modal.Title>
          </Modal.Header>
          <Modal.Body className="text-center">
            {ticket.qr_code ? (
              <div>
                <img src={ticket.qr_code} alt="QR Code" style={{ maxWidth: '100%' }} />
                <p className="mt-3 mb-0">
                  <small className="text-muted">{ticket.ticket_number}</small>
                </p>
              </div>
            ) : (
              <p>QR 코드가 없습니다.</p>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowQRModal(false)}>
              닫기
            </Button>
          </Modal.Footer>
        </Modal>

        {/* 취소 확인 모달 */}
        <Modal show={showCancelModal} onHide={() => setShowCancelModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>티켓 취소 확인</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            정말로 이 티켓을 취소하시겠습니까?
            <div className="mt-3">
              <p><strong>티켓번호:</strong> {ticket.ticket_number}</p>
              <p><strong>예약번호:</strong> {ticket.booking?.booking_number || '-'}</p>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCancelModal(false)}>
              닫기
            </Button>
            <Button
              variant="danger"
              onClick={() => cancelMutation.mutate()}
              disabled={cancelMutation.isPending}
            >
              {cancelMutation.isPending ? '취소 중...' : '취소하기'}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>

      <style>{`
        .detail-item {
          padding: 8px 0;
        }
        .detail-label {
          display: block;
          font-size: 12px;
          color: var(--color-text-secondary);
          margin-bottom: 4px;
          font-weight: 500;
        }
        .detail-value {
          font-size: 14px;
          color: var(--color-text-primary);
        }
      `}</style>
    </AdminLayout>
  );
};

export default TicketDetail;

